import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ImageViewer.css';

const ImageViewer = ({ src, alt, className = '' }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageError, setImageError] = useState(false);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!containerRef.current || !imageRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // More responsive zoom factor
    const zoomIntensity = 0.15;
    const delta = e.deltaY > 0 ? -zoomIntensity : zoomIntensity;
    const newScale = Math.min(Math.max(scale * (1 + delta), 0.3), 8);
    
    if (newScale === scale) return; // No change needed
    
    // Calculate zoom point relative to current transform
    const scaleRatio = newScale / scale;
    
    // Calculate new position to keep zoom centered on mouse
    const newX = mouseX - (mouseX - position.x) * scaleRatio;
    const newY = mouseY - (mouseY - position.y) * scaleRatio;
    
    setScale(newScale);
    setPosition({ x: newX, y: newY });
  }, [scale, position]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const openBlueprintModal = () => {
    setShowBlueprintModal(true);
  };

  const closeBlueprintModal = () => {
    setShowBlueprintModal(false);
  };

  useEffect(() => {
    const handleMouseMoveGlobal = (e) => handleMouseMove(e);
    const handleMouseUpGlobal = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMoveGlobal);
      document.addEventListener('mouseup', handleMouseUpGlobal);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveGlobal);
      document.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  }, [isDragging, dragStart, handleMouseMove]);

  // Add wheel event listener to prevent page scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheelCapture = (e) => {
      if (!imageError) {
        handleWheel(e);
      }
    };

    container.addEventListener('wheel', handleWheelCapture, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheelCapture);
    };
  }, [imageError, handleWheel]);

  return (
    <>
      <div className={`image-viewer ${className}`}>
        <div
          ref={containerRef}
          className="image-viewer-container"
          onMouseDown={imageError ? undefined : handleMouseDown}
        >
          {imageError ? (
            <div className="blueprint-placeholder" onClick={openBlueprintModal}>
              <div className="blueprint-preview">
                <h3>🎮 Magnet Gun Blueprint</h3>
                <div className="blueprint-nodes-preview">
                  <div className="node-preview input-node">🎯 Target Detection</div>
                  <div className="node-preview process-node">⚡ Magnetic Force</div>
                  <div className="node-preview output-node">🧲 Apply Force</div>
                </div>
                <p>Click to view blueprint details</p>
              </div>
            </div>
          ) : (
            <img
              ref={imageRef}
              src={src}
              alt={alt}
              className="zoomable-image"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
              draggable={false}
              onError={handleImageError}
            />
          )}
        </div>
        
        {!imageError && (
          <div className="image-viewer-instructions">
            <p>🖱️ Scroll to zoom • Drag to pan</p>
          </div>
        )}
      </div>

      {/* Blueprint Modal */}
      {showBlueprintModal && (
        <div className="blueprint-modal-overlay" onClick={closeBlueprintModal}>
          <div className="blueprint-modal" onClick={(e) => e.stopPropagation()}>
            <button className="blueprint-modal-close" onClick={closeBlueprintModal}>
              ×
            </button>
            <div className="blueprint-content">
              <div className="blueprint-grid">
                <h3>🎮 Magnet Gun Blueprint</h3>
                <div className="blueprint-nodes">
                  <div className="node input-node">🎯 Target Detection</div>
                  <div className="node process-node">⚡ Magnetic Force Calculation</div>
                  <div className="node output-node">🧲 Apply Force Vector</div>
                </div>
                <div className="blueprint-connections">
                  <div className="connection-line"></div>
                  <div className="connection-line"></div>
                </div>
                <p>Blueprint visualization showing the core components of the magnet gun system.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageViewer;